import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { copyText } from '../lib/clipboard.js';
import { tvLinkModel, type TvConfig } from './tvLinkModel.js';
import styles from './JoinPanel.module.css';

/**
 * The console's "Players join" card: the QR a guest scans off the owner's
 * screen, the same URL written out, and the join code for anyone typing it in.
 * It reuses `tvLinkModel` so the address is one a phone on the wifi can
 * actually open — a localhost console would hand out a dead link.
 *
 * This survives the TV-per-table split: a table's TV is a screen in the room,
 * while this is how a guest's own phone gets to the floor. Opening it here is
 * worth a button — unlike a TV page, the owner's browser is a fine place to
 * check what the guests are about to see.
 *
 * The TV shows this too, but the night starts before the TV is up, and the
 * owner's laptop is already in the room.
 */
export function JoinPanel({
  playPath,
  joinCode,
  config,
  origin = window.location.origin,
}: {
  playPath: string;
  joinCode: string;
  /** `/api/config`, fetched once by the console and handed to every card. */
  config: TvConfig | null;
  origin?: string;
}) {
  const [copied, setCopied] = useState<boolean | null>(null);

  const vm = tvLinkModel({ origin, tvPath: playPath, config, apple: null });

  return (
    <div className={styles.card}>
      <p className={styles.heading}>Players join</p>
      <div className={styles.row}>
        <div className={styles.qr}>
          <QRCodeSVG value={vm.openUrl} size={104} bgColor="#f5efe2" fgColor="#06231a" level="M" />
        </div>
        <div className={styles.codeSide}>
          <p className={styles.codeLabel}>Join code</p>
          <p className={styles.code}>{joinCode}</p>
          <p className={styles.url}>{vm.openUrlShort}</p>
        </div>
      </div>
      <div className={styles.buttons}>
        <button
          type="button"
          className={styles.button}
          onClick={() => void copyText(vm.openUrl).then(setCopied)}
        >
          {copied === null ? 'Copy join link' : copied ? 'Copied' : 'Copy failed'}
        </button>
        <a className={styles.button} href={vm.openUrl} target="_blank" rel="noreferrer">
          Open join page
        </a>
      </div>
      <p className={styles.hint}>
        Scan it, or send the link. Your own phone joins the same way — as the owner it comes
        with the host controls.
      </p>
    </div>
  );
}
